import { VirtualizedGrid } from "@arcadeai/design-system/components/ui/atoms/virtualized-grid";
import { EmptyState } from "@arcadeai/design-system/components/ui/molecules/empty-state";
import { useColumns } from "@arcadeai/design-system/hooks/use-columns";
import { useMemo } from "react";
import { FEATURED_TOOLKITS } from "../hooks/featured-toolkits";
import { useToolkitPicker } from "../hooks/use-toolkit-picker";
import type { SelectionMode } from "../index";
import { EmptyStateCTAs } from "./empty-state-ctas";
import { ToolkitCardWithSelection } from "./toolkit-card-with-selection";
import { UnoptimizedToggle } from "./unoptimized-toggle";

// Toolkit card height (~140px) + row gap (12px) + padding buffer (4px)
const TOOLKIT_CARD_ROW_HEIGHT = 156;

type ToolkitListProps = {
  selectionMode: SelectionMode;
  scrollContainer: HTMLElement | null;
};

export function ToolkitList({
  selectionMode,
  scrollContainer,
}: ToolkitListProps) {
  const {
    state: {
      filteredToolkits,
      selectedToolkits,
      selectedTools,
      searchQuery,
      showUnoptimizedToolkits,
      hasStarterToolkits,
      hiddenToolkitCount,
    },
    toggleToolkit,
    setActiveTab,
    setToolkitFilter,
    toggleUnoptimizedToolkits,
  } = useToolkitPicker();

  const columns = useColumns();

  // Split toolkits into selected, featured (non-selected), and unselected
  const { selectedToolkitItems, featuredToolkitItems, unselectedToolkitItems } =
    useMemo(() => {
      const toolkitsArray = Array.from(filteredToolkits);
      const featuredSet = new Set(
        FEATURED_TOOLKITS.map((name) => name.toLowerCase())
      );

      const selected = toolkitsArray.filter((tk) =>
        selectedToolkits.has(tk.name)
      );
      const featured = toolkitsArray.filter(
        (tk) =>
          !(selectedToolkits.has(tk.name) || searchQuery.trim()) &&
          featuredSet.has(tk.name.toLowerCase())
      );
      const unselected = toolkitsArray.filter(
        (tk) =>
          !selectedToolkits.has(tk.name) &&
          (searchQuery.trim() || !featuredSet.has(tk.name.toLowerCase()))
      );

      return {
        selectedToolkitItems: selected,
        featuredToolkitItems: featured,
        unselectedToolkitItems: unselected,
      };
    }, [filteredToolkits, searchQuery, selectedToolkits]);

  if (filteredToolkits.size === 0) {
    // If there are hidden toolkits that match the search, show the toggle instead of empty state
    if (hasStarterToolkits && hiddenToolkitCount > 0) {
      return (
        <UnoptimizedToggle
          hiddenCount={hiddenToolkitCount}
          isShowingUnoptimized={showUnoptimizedToolkits}
          itemType="toolkit"
          onToggle={toggleUnoptimizedToolkits}
        />
      );
    }

    return (
      <EmptyState
        data-testid="empty-toolkits"
        description="Try a different search term"
        title="No MCP servers found"
      >
        <EmptyStateCTAs />
      </EmptyState>
    );
  }

  const renderToolkitCard = (tk: (typeof selectedToolkitItems)[number]) => (
    <ToolkitCardWithSelection
      key={tk.name}
      onSelect={(_checked) => {
        if (selectionMode === "single") {
          setActiveTab("tools");
          setToolkitFilter(tk.name);
          return;
        }
        toggleToolkit(tk);
      }}
      onView={() => {
        setToolkitFilter(tk.name);
        setActiveTab("tools");
      }}
      selectedTools={new Set(selectedTools.keys())}
      selectionMode={selectionMode}
      toolkit={tk}
    />
  );

  return (
    <div className="space-y-4" data-testid="toolkits-grid">
      {selectedToolkitItems.length > 0 && (
        <div className="space-y-3" data-testid="selected-toolkits-section">
          <div className="flex items-center gap-1.5">
            <h3 className="font-semibold text-neutral-700 text-sm dark:text-neutral-200">
              Selected
            </h3>
            <span className="rounded-full bg-emerald-300 px-1.5 py-0.5 text-emerald-900 text-xs dark:bg-emerald-500/25 dark:text-emerald-200">
              {selectedToolkitItems.length}
            </span>
          </div>
          <VirtualizedGrid
            columns={columns}
            estimateRowSize={TOOLKIT_CARD_ROW_HEIGHT}
            getItemKey={(tk) => tk.name}
            gridClassName="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3"
            items={selectedToolkitItems}
            renderItem={renderToolkitCard}
            rowGapPx={12}
            scrollElement={scrollContainer}
          />
        </div>
      )}

      {/* Featured Section - hidden when searching or when user has none of the featured toolkits */}
      {!searchQuery.trim() && featuredToolkitItems.length > 0 && (
        <div
          className="space-y-3 rounded-lg bg-neutral-50 p-4 dark:bg-neutral-900"
          data-testid="featured-toolkits-section"
        >
          <div className="flex items-center gap-1.5">
            <h3 className="font-semibold text-neutral-700 text-sm dark:text-neutral-200">
              Featured MCP Servers
            </h3>
            <span className="rounded-full bg-neutral-200 px-1.5 py-0.5 text-neutral-800 text-xs dark:bg-neutral-800 dark:text-neutral-200">
              {featuredToolkitItems.length}
            </span>
          </div>
          <VirtualizedGrid
            columns={columns}
            estimateRowSize={TOOLKIT_CARD_ROW_HEIGHT}
            getItemKey={(tk) => tk.name}
            gridClassName="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3"
            items={featuredToolkitItems}
            renderItem={renderToolkitCard}
            rowGapPx={12}
            scrollElement={scrollContainer}
          />
        </div>
      )}

      {unselectedToolkitItems.length > 0 && (
        <div className="space-y-3">
          {(selectedToolkitItems.length > 0 ||
            featuredToolkitItems.length > 0) && (
            <div className="flex items-center gap-1.5">
              <h3 className="font-semibold text-neutral-700 text-sm dark:text-neutral-200">
                All MCP servers
              </h3>
              <span className="rounded-full bg-neutral-200 px-1.5 py-0.5 text-neutral-800 text-xs dark:bg-neutral-800 dark:text-neutral-200">
                {unselectedToolkitItems.length}
              </span>
            </div>
          )}
          <VirtualizedGrid
            columns={columns}
            estimateRowSize={TOOLKIT_CARD_ROW_HEIGHT}
            getItemKey={(tk) => tk.name}
            gridClassName="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3"
            items={unselectedToolkitItems}
            renderItem={renderToolkitCard}
            rowGapPx={12}
            scrollElement={scrollContainer}
          />
        </div>
      )}

      {/* Hidden results message and toggle */}
      {!!hasStarterToolkits && (
        <UnoptimizedToggle
          hiddenCount={hiddenToolkitCount}
          isShowingUnoptimized={showUnoptimizedToolkits}
          itemType="toolkit"
          onToggle={toggleUnoptimizedToolkits}
        />
      )}
    </div>
  );
}
